// pages/admin/AddTestimonial.js
import React, { useRef, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  styled,
  IconButton,
  Dialog,
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Fade,
  Container,
  Divider,
} from "@mui/material";
import {
  Home,
  RateReview,
  Add,
  Delete,
  Edit,
  Close,
  CloudUpload,
  ArrowForward,
  School,
  Person,
  Email,
  Description,
  Verified,
} from "@mui/icons-material";
import { uploadFileService } from "../../services/Request";
import { postData, getData } from "../../services/Request";
import CircularProgress from "@mui/material/CircularProgress";
import { useLoader } from "../../context/LoaderContext";
import Swal from "sweetalert2";
import AdminSideBar from "./components/AdminSideBar";
import "../../styles/AddCourse.css";

const rowsPerPageOptions = [10, 20, 30];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 14,
  color: "#ffffff",
  backgroundColor: "#FFD93D",
  padding: "12px 16px",
  "&:first-of-type": {
    borderTopLeftRadius: 8,
  },
  "&:last-of-type": {
    borderTopRightRadius: 8,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(247, 248, 242, 0.5)",
  },
  "&:hover": {
    backgroundColor: "rgba(255, 217, 61, 0.04)",
    transition: "background-color 0.2s ease",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const AddTestimonial = () => {
  const fileInputRef = useRef(null);
  const { showLoader, hideLoader } = useLoader();
  const [imageUrl, setImageUrl] = useState("");
  const [loader, setLoader] = useState(false);
  const [userData, setUserData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        if (data.usertype !== "Admin") {
          window.location.href = "/admin";
        } else {
          setUserInfo(data);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    getAllTestimonials();
  }, []);

  const getAllTestimonials = () => {
    showLoader();
    getData("getAllTestimonials")
      .then((response) => {
        setUserData(response.testimonials || []);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size / 1024 / 1024 > 1) {
      Swal.fire({
        title: "File too large",
        text: "File size exceeds 1 MB. Please select a smaller file.",
        icon: "warning",
        confirmButtonColor: "#FFD93D",
        background: "#ffffff",
        color: "#1a1a1a",
      });
      return;
    }

    setUploading(true);
    uploadFileService(file)
      .then((response) => {
        setUploading(false);
        if (response.status === true) {
          setImageUrl(response.location);
          Swal.fire({
            title: "Uploaded!",
            text: "Image uploaded successfully.",
            icon: "success",
            confirmButtonColor: "#FFD93D",
            background: "#ffffff",
            color: "#1a1a1a",
          });
        } else {
          Swal.fire({
            title: "Error",
            text: "Error uploading image. Please try again.",
            icon: "error",
            confirmButtonColor: "#FF6B6B",
            background: "#ffffff",
            color: "#1a1a1a",
          });
        }
      })
      .catch((error) => {
        setUploading(false);
        Swal.fire({
          title: "Error",
          text: "Error uploading image. Please try again.",
          icon: "error",
          confirmButtonColor: "#FF6B6B",
          background: "#ffffff",
          color: "#1a1a1a",
        });
      });
  };

  const handleSubmit = (e) => {
    setLoader(true);
    e.preventDefault();

    const name = e.target.name.value;
    const title = e.target.title.value;
    const description = e.target.description.value;

    if (!imageUrl && modalType === "add") {
      Swal.fire({
        title: "Image Required",
        text: "Please upload an image for the testimonial.",
        icon: "warning",
        confirmButtonColor: "#FFD93D",
        background: "#ffffff",
        color: "#1a1a1a",
      });
      setLoader(false);
      return;
    }

    const details = {
      name: name,
      title: title,
      description: description,
      image: imageUrl || selectedTestimonial?.image || "",
    };

    const endpoint =
      modalType === "add" ? "addTestimonial" : "updateTestimonial";
    if (modalType === "edit" && selectedTestimonial) {
      details.id = selectedTestimonial.id;
    }

    postData(endpoint, details)
      .then((response) => {
        setLoader(false);
        if (response.status === true) {
          setOpenModal(false);
          setImageUrl("");
          Swal.fire({
            title: "Success!",
            text: `Testimonial ${modalType === "add" ? "added" : "updated"} successfully.`,
            icon: "success",
            confirmButtonColor: "#FFD93D",
            background: "#ffffff",
            color: "#1a1a1a",
          });
          getAllTestimonials();
        } else {
          Swal.fire({
            title: "Error",
            text: response.message || "Error saving testimonial.",
            icon: "error",
            confirmButtonColor: "#FF6B6B",
            background: "#ffffff",
            color: "#1a1a1a",
          });
        }
      })
      .catch((error) => {
        setLoader(false);
        Swal.fire({
          title: "Error",
          text: "Error saving testimonial. Please try again.",
          icon: "error",
          confirmButtonColor: "#FF6B6B",
          background: "#ffffff",
          color: "#1a1a1a",
        });
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const slicedData = userData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleOpenModal = (type, data = null) => {
    setModalType(type);
    setSelectedTestimonial(data);
    if (type === "edit" && data) {
      setImageUrl(data.image || "");
    } else {
      setImageUrl("");
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedTestimonial(null);
    setImageUrl("");
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete Testimonial",
      text: "Are you sure you want to delete this testimonial?",
      icon: "warning",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      denyButtonText: "Cancel",
      confirmButtonColor: "#FF6B6B",
      denyButtonColor: "#FFD93D",
      background: "#ffffff",
      color: "#1a1a1a",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleted!",
          text: "Testimonial has been deleted successfully.",
          icon: "success",
          confirmButtonColor: "#FFD93D",
          background: "#ffffff",
          color: "#1a1a1a",
        });
        postData("deleteTestimonial", { id: id }).then(() => {
          getAllTestimonials();
        });
      }
    });
  };

  const stats = [
    {
      icon: <RateReview sx={{ fontSize: 24 }} />,
      label: "Total Testimonials",
      value: userData.length,
      color: "#FFD93D",
      bgColor: "rgba(255, 217, 61, 0.08)",
    },
    {
      icon: <Verified sx={{ fontSize: 24 }} />,
      label: "Verified",
      value: userData.length,
      color: "#2BAD65",
      bgColor: "rgba(43, 173, 101, 0.08)",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#F7F8F2",
      }}
    >
      {/* Admin Sidebar */}
      <Box
        sx={{
          width: { xs: 0, md: 280 },
          flexShrink: 0,
          display: { xs: "none", md: "block" },
        }}
      >
        <AdminSideBar />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 2, md: 3 },
          }}
        >
          <Container maxWidth="xl" sx={{ p: 0 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs
                separator={
                  <ArrowForward
                    sx={{ fontSize: 14, color: "rgba(26,26,26,0.2)" }}
                  />
                }
                aria-label="breadcrumb"
                sx={{ mb: 1 }}
              >
                <Link
                  underline="hover"
                  color="rgba(26, 26, 26, 0.4)"
                  href="/admin/dashboard"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.85rem",
                    "&:hover": {
                      color: "#FFD93D",
                    },
                  }}
                >
                  <Home sx={{ fontSize: 16 }} />
                  Home
                </Link>
                <Typography
                  sx={{
                    color: "#FFD93D",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <RateReview sx={{ fontSize: 16 }} />
                  Testimonials
                </Typography>
              </Breadcrumbs>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a1a",
                      fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                  >
                    Testimonials
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(26, 26, 26, 0.4)",
                      mt: 0.5,
                    }}
                  >
                    Manage student testimonials and reviews
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    icon={<School sx={{ fontSize: 16 }} />}
                    label={`Admin: ${userInfo?.firstname || "Admin"}`}
                    sx={{
                      backgroundColor: "rgba(255, 217, 61, 0.08)",
                      color: "#FFD93D",
                      fontWeight: 600,
                      border: "1px solid rgba(255, 217, 61, 0.1)",
                    }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenModal("add")}
                    sx={{
                      backgroundColor: "#FFD93D",
                      color: "#1a1a1a",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "#F6C90E",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 20px rgba(255, 217, 61, 0.3)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Add Testimonial
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {stats.map((stat, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Fade in timeout={400 + index * 100}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        backgroundColor: "#ffffff",
                        border: "1px solid rgba(255, 217, 61, 0.04)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 30px rgba(255, 217, 61, 0.06)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            backgroundColor: stat.bgColor,
                            borderRadius: "50%",
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: stat.color,
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: "#1a1a1a",
                              fontSize: "1.5rem",
                              lineHeight: 1.2,
                            }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "rgba(26, 26, 26, 0.4)",
                              fontSize: "0.75rem",
                            }}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Fade>
                </Grid>
              ))}
            </Grid>

            {/* Table */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(255, 217, 61, 0.06)",
                backgroundColor: "#ffffff",
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>#</StyledTableCell>
                      <StyledTableCell>Image</StyledTableCell>
                      <StyledTableCell>Name</StyledTableCell>
                      <StyledTableCell>Title</StyledTableCell>
                      <StyledTableCell>Description</StyledTableCell>
                      <StyledTableCell align="center">Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slicedData.length > 0 ? (
                      slicedData.map((row, index) => (
                        <StyledTableRow key={row.id} hover>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              color: "rgba(26,26,26,0.4)",
                            }}
                          >
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell>
                            <Avatar
                              src={row.image}
                              alt={row.name}
                              sx={{ width: 50, height: 50, borderRadius: 2 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontWeight: 500, color: "#1a1a1a" }}
                            >
                              {row.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.title}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(255, 217, 61, 0.1)",
                                color: "#FFD93D",
                                fontWeight: 500,
                                fontSize: "0.7rem",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "rgba(26, 26, 26, 0.6)",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.description}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="center"
                            >
                              <Tooltip title="Edit Testimonial">
                                <IconButton
                                  onClick={() => handleOpenModal("edit", row)}
                                  sx={{
                                    color: "#FFD93D",
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(255, 217, 61, 0.08)",
                                    },
                                  }}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Testimonial">
                                <IconButton
                                  onClick={() => handleDelete(row.id)}
                                  sx={{
                                    color: "#FF6B6B",
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(255, 107, 107, 0.08)",
                                    },
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <RateReview
                            sx={{ fontSize: 48, color: "rgba(26,26,26,0.1)" }}
                          />
                          <Typography
                            sx={{ color: "rgba(26,26,26,0.3)", mt: 1 }}
                          >
                            No testimonials found
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenModal("add")}
                            sx={{
                              mt: 2,
                              backgroundColor: "#FFD93D",
                              color: "#1a1a1a",
                              "&:hover": {
                                backgroundColor: "#F6C90E",
                              },
                            }}
                          >
                            Add Your First Testimonial
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                component="div"
                count={userData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid rgba(255, 217, 61, 0.06)",
                  "& .MuiTablePagination-select": {
                    color: "#1a1a1a",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    color: "rgba(26,26,26,0.4)",
                  },
                }}
              />
            </Paper>
          </Container>
        </Box>
      </Box>

      {/* Add/Edit Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 3,
            backgroundColor: "#ffffff",
            border: "1px solid rgba(255, 217, 61, 0.1)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
            {modalType === "add" ? "Add Testimonial" : "Edit Testimonial"}
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3, backgroundColor: "rgba(255, 217, 61, 0.1)" }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Image Upload */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#1a1a1a", mb: 1 }}
              >
                Testimonial Image
              </Typography>
              <Box
                onClick={handleUploadClick}
                sx={{
                  border: "2px dashed rgba(255, 217, 61, 0.3)",
                  borderRadius: 3,
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundColor: imageUrl
                    ? "rgba(255, 217, 61, 0.02)"
                    : "transparent",
                  "&:hover": {
                    borderColor: "#FFD93D",
                    backgroundColor: "rgba(255, 217, 61, 0.04)",
                  },
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/gif"
                />

                {imageUrl ? (
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <img
                      src={imageUrl}
                      alt="Testimonial"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        borderRadius: 8,
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ) : (
                  <Box>
                    {uploading ? (
                      <CircularProgress sx={{ color: "#FFD93D" }} />
                    ) : (
                      <>
                        <CloudUpload
                          sx={{
                            fontSize: 48,
                            color: "rgba(255, 217, 61, 0.3)",
                            mb: 1,
                          }}
                        />
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "#FFD93D" }}
                        >
                          Click to upload
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                        >
                          or drag and drop
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(26, 26, 26, 0.2)",
                            display: "block",
                            mt: 1,
                          }}
                        >
                          PNG, JPG or GIF (max. 1MB)
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="name"
                label="Full Name"
                required
                defaultValue={selectedTestimonial?.name || ""}
                InputProps={{
                  startAdornment: (
                    <Person sx={{ color: "rgba(26, 26, 26, 0.2)", mr: 1 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 217, 61, 0.02)",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "rgba(255, 217, 61, 0.1)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 217, 61, 0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#FFD93D",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(26, 26, 26, 0.4)",
                  },
                }}
              />
            </Grid>

            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="title"
                label="Title / Position"
                required
                defaultValue={selectedTestimonial?.title || ""}
                InputProps={{
                  startAdornment: (
                    <Email sx={{ color: "rgba(26, 26, 26, 0.2)", mr: 1 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 217, 61, 0.02)",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "rgba(255, 217, 61, 0.1)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 217, 61, 0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#FFD93D",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(26, 26, 26, 0.4)",
                  },
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label="Testimonial"
                required
                multiline
                rows={4}
                defaultValue={selectedTestimonial?.description || ""}
                placeholder="Write the testimonial message..."
                InputProps={{
                  startAdornment: (
                    <Description
                      sx={{ color: "rgba(26, 26, 26, 0.2)", mr: 1 }}
                    />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 217, 61, 0.02)",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "rgba(255, 217, 61, 0.1)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 217, 61, 0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#FFD93D",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(26, 26, 26, 0.4)",
                  },
                }}
              />
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    color: "rgba(26, 26, 26, 0.6)",
                    borderColor: "rgba(255, 217, 61, 0.2)",
                    "&:hover": {
                      borderColor: "#FFD93D",
                      color: "#FFD93D",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loader || uploading}
                  sx={{
                    backgroundColor: "#FFD93D",
                    color: "#1a1a1a",
                    fontWeight: 600,
                    px: 4,
                    "&:hover": {
                      backgroundColor: "#F6C90E",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(255, 217, 61, 0.3)",
                    },
                  }}
                >
                  {loader ? (
                    <CircularProgress size={24} sx={{ color: "#1a1a1a" }} />
                  ) : (
                    "Save"
                  )}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Dialog>
    </Box>
  );
};

export default AddTestimonial;
